import { Injectable } from '@angular/core';
import { AbstractSecurityStorage } from 'angular-auth-oidc-client';
import { RETURN_PAGE_KEY } from './auth.config';

/**
 * Storage for angular-auth-oidc-client that keeps tokens and user data out of the browser's storage
 * (BSI TR-03161-2 O.Arch_4, O.Auth_13, O.Data_14).
 *
 * The library keeps everything of one configuration as a single JSON object under one storage key.
 * The entries that hold tokens or user data live in memory only; the entries the login redirect needs
 * (PKCE code verifier, state, nonce, ...) stay in sessionStorage, because the page is reloaded when the
 * user returns from the identity provider. A page reload therefore drops the tokens and the app signs
 * in again with the identity provider's session.
 */
const IN_MEMORY_ONLY = [
    'authnResult', // id token, access token and refresh token
    'authzData', // access token
    'userData', // claims of the user (name, e-mail, ...)
    'access_token_expires_at',
    'reusable_refresh_token',
];

@Injectable()
export class TokenSafeStorage implements AbstractSecurityStorage {
    private _secrets = new Map<string, Record<string, unknown>>();

    constructor() {
        this._moveLegacyTokensToMemory();
    }

    read(key: string): string | null {
        const persistent = this._readSession(key);
        const secrets = this._secrets.get(key);

        if (persistent === null && !secrets) {
            return null;
        }
        return JSON.stringify({ ...(persistent ?? {}), ...(secrets ?? {}) });
    }

    write(key: string, value: string): void {
        let entries: Record<string, unknown>;
        try {
            const parsed = JSON.parse(value);
            if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
                throw new Error('not an object');
            }
            entries = parsed;
        } catch {
            // Not the library's object format: nothing here can be a token entry.
            this._writeSession(key, value);
            return;
        }

        const { persistent, secrets } = this._split(entries);

        if (Object.keys(secrets).length) {
            this._secrets.set(key, secrets);
        } else {
            this._secrets.delete(key);
        }
        this._writeSession(key, JSON.stringify(persistent));
    }

    remove(key: string): void {
        this._secrets.delete(key);
        try {
            sessionStorage.removeItem(key);
        } catch {
            // storage unavailable
        }
    }

    clear(): void {
        this._secrets.clear();
        try {
            // The page to come back to is not the library's state. A sign-out that the session forces sets it just before
            // the library clears its storage, and the next sign-in needs it.
            const returnPage = sessionStorage.getItem(RETURN_PAGE_KEY);
            sessionStorage.clear();
            if (returnPage !== null) {
                sessionStorage.setItem(RETURN_PAGE_KEY, returnPage);
            }
        } catch {
            // storage unavailable
        }
    }

    private _split(entries: Record<string, unknown>) {
        const persistent: Record<string, unknown> = {};
        const secrets: Record<string, unknown> = {};
        for (const [name, entry] of Object.entries(entries)) {
            (IN_MEMORY_ONLY.includes(name) ? secrets : persistent)[name] = entry;
        }
        return { persistent, secrets };
    }

    private _readSession(key: string): Record<string, unknown> | null {
        try {
            const raw = sessionStorage.getItem(key);
            if (raw === null) {
                return null;
            }
            const parsed = JSON.parse(raw);
            return parsed !== null && typeof parsed === 'object' ? parsed : {};
        } catch {
            return null;
        }
    }

    private _writeSession(key: string, value: string): void {
        try {
            if (value === '{}') {
                sessionStorage.removeItem(key);
            } else {
                sessionStorage.setItem(key, value);
            }
        } catch {
            // storage unavailable: the redirect flow state is lost, the user simply signs in again
        }
    }

    /**
     * Sessions that were started before this storage existed still have their tokens in sessionStorage.
     * Move them to memory so those users stay signed in and nothing is left behind in the browser.
     */
    private _moveLegacyTokensToMemory(): void {
        try {
            const keys = Array.from({ length: sessionStorage.length }, (_, i) => sessionStorage.key(i));
            for (const key of keys) {
                if (key === null) {
                    continue;
                }
                const entries = this._readSession(key);
                if (!entries || !IN_MEMORY_ONLY.some((name) => name in entries)) {
                    continue;
                }
                const { persistent, secrets } = this._split(entries);
                this._secrets.set(key, secrets);
                this._writeSession(key, JSON.stringify(persistent));
            }
        } catch {
            // storage unavailable
        }
    }
}
